import { Box, FormControl, FormLabel, Grid, OutlinedInput, Button, FormHelperText, TextField, Typography } from '@mui/material'
import { useEffect, useRef, useState, useMemo } from "react";
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Select from 'react-select';
import { getReportRequest, postReportRequest, putReportRequest, resetGetReport } from '../../../../redux/modules/report/slice';
import { getExamGroupIdRequest, resetGetExamGroupId } from '../../../../redux/modules/exam/slice';
import { useDispatch, useSelector } from 'react-redux';
import { USER_ROLE_DEFAULT } from '../../../../constants/Constant';

// All ultrasound exam categories - same as QuickCreateDialog
const ULTRASOUND_CATEGORIES = [
    'OB/GYN Ultrasound',
    'Abdominal Ultrasound',
    'Vascular Ultrasound',
    'Musculoskeletal Ultrasound',
    'Small Parts Ultrasound',
    'Pediatric Ultrasound',
    'Interventional/Guidance',
];

const validationSchema = yup.object().shape({
    completedExam: yup.array(),
    clinicalInfo: yup.string(),
    lmp: yup.mixed().nullable().transform((value, originalValue) => {
        // Allow empty, null, or "N/A" values
        if (originalValue === '' || originalValue === null || originalValue === undefined || originalValue === 'N/A') {
            return null;
        }
        return originalValue;
    }),
    reportStatus: yup.string(),

});

export default function ExamsRadiologyReport({ itemData, setSaveType }) {
    const dispatch = useDispatch();
    let { currentBranch } = useSelector(state => state.branch);
    let { userData } = useSelector(state => state.auth);
    let { loading, getReportRes } = useSelector(state => state.report);
    let { getGroupIdRes } = useSelector(state => state.exam);
    const formRef = useRef(null);

    // Initialize with exams from itemData.examList (if any)
    const initialExams = useMemo(() => {
        return itemData?.examList ? itemData.examList.map((item) => item.list).flat() : [];
    }, [itemData?.examList]);

    const [completedExam, setCompletedExam] = useState(initialExams);

    const { register, handleSubmit, formState: { errors }, setValue } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            // LMP: 0 means not pregnant, null/undefined means not set
            lmp: itemData?.lmp === 0 ? '' : (itemData?.lmp || ''),
            completedExam: initialExams.map(item => item._id),
            reportStatus : itemData?.reportData?.reportStatus,
        },
    });

    // Fetch all exam groups on mount
    useEffect(() => {
        if (currentBranch) {
            dispatch(getExamGroupIdRequest(ULTRASOUND_CATEGORIES));
        }
        return () => {
            dispatch(resetGetExamGroupId());
        };
    }, [dispatch, currentBranch]);

    useEffect(() => {
        if (!currentBranch) { return }
        if (itemData.reportData) { dispatch(getReportRequest(itemData.reportData?._id)); }
    }, [dispatch, currentBranch, itemData]);

    // Build all available exams from the fetched groups
    const allAvailableExams = useMemo(() => {
        if (!getGroupIdRes?.data) return initialExams;

        // Flatten all exams from all groups
        const allExams = getGroupIdRes.data.flatMap(group =>
            group.list?.map(exam => ({
                ...exam,
                groupName: group.name,
                groupId: group._id
            })) || []
        );

        return allExams;
    }, [getGroupIdRes?.data, initialExams]);

    // Store the report completed exam IDs to re-apply when allAvailableExams loads
    const [reportCompletedExamIds, setReportCompletedExamIds] = useState([]);
    const [hasLoadedReport, setHasLoadedReport] = useState(false);

    useEffect(() => {
        if (getReportRes) {
            if (getReportRes?.success) {
                const examIds = getReportRes?.data?.completedExam || [];
                setReportCompletedExamIds(examIds);
                setValue("completedExam", examIds);
                setValue("clinicalInfo", getReportRes?.data?.clinicalInfo);
                setHasLoadedReport(true);
            }
            dispatch(resetGetReport())
        }
    }, [dispatch, getReportRes, setValue]);

    // Update completedExam when allAvailableExams loads or reportCompletedExamIds changes
    useEffect(() => {
        // If we have report data loaded, use those IDs
        if (hasLoadedReport && reportCompletedExamIds.length > 0 && allAvailableExams.length > 0) {
            // Find the exam objects from all available exams
            const completedExamObjs = allAvailableExams.filter(
                exam => reportCompletedExamIds.includes(exam._id)
            );

            if (completedExamObjs.length > 0) {
                setCompletedExam(completedExamObjs);
            } else if (itemData?.examList) {
                // Fallback to itemData.examList if not found in allAvailableExams
                const examData = itemData.examList.map((item) => item.list).flat();
                const fallbackExams = examData.filter(item => reportCompletedExamIds.includes(item._id));
                setCompletedExam(fallbackExams);
            }
        }
        // If no report yet (new report), pre-populate with ordered exams
        else if (!itemData?.reportData && initialExams.length > 0 && allAvailableExams.length > 0) {
            // Find matching exam objects from allAvailableExams for better display
            const matchedExams = allAvailableExams.filter(
                exam => initialExams.some(ie => ie._id === exam._id)
            );
            if (matchedExams.length > 0) {
                setCompletedExam(matchedExams);
                setValue("completedExam", matchedExams.map(e => e._id));
            }
        }
    }, [reportCompletedExamIds, allAvailableExams, itemData?.examList, itemData?.reportData, hasLoadedReport, initialExams, setValue])

    const handlepatientSelect = (selected) => {
        if (selected) {
            setCompletedExam(selected)
            setValue("completedExam", selected.map(item => item._id));
        }
    };

    const onSubmit = (data) => {
        data.radiologyId = itemData?.radiologyId;
        if (itemData?.reportData?._id) {
            data.reportId = itemData?.reportData?._id;
            dispatch(putReportRequest(data));
        } else {
            dispatch(postReportRequest(data));
        }
    };
    const selectStyles = {
        control: (base, state) => ({
            ...base,
            height: state.isDisabled ? '56px' : 'auto',
            minHeight: '56px'
        }),
    };
    return (
        <Box component={"form"} onSubmit={handleSubmit(onSubmit)} ref={formRef}>
            <Grid container spacing={2}>
                <Grid item lg={6} md={6} sm={6} xs={12} >
                    <FormControl fullWidth className='selectDropdown'>
                        <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>Exams Completed</FormLabel>
                        <Select
                            isDisabled={userData.role === USER_ROLE_DEFAULT.PATIENT}
                            value={completedExam}
                            autoFocus={false}
                            isMulti={true}
                            onChange={handlepatientSelect}
                            options={allAvailableExams}
                            getOptionValue={(option) => option._id}
                            getOptionLabel={(option) => option.groupName ? `${option.name} (${option.groupName})` : option.name}
                            isSearchable={true}
                            placeholder="Search and select exams..."
                            noOptionsMessage={() => "Loading exams..."}
                            styles={selectStyles}
                            />
                        {allAvailableExams.length === 0 && initialExams.length === 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                Loading available exams...
                            </Typography>
                        )}
                        {errors?.completedExam?.message && <FormHelperText>{errors?.completedExam?.message}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12} className='girdMarginBottom'>
                    <FormControl fullWidth className='inputfield' sx={{ marginTop: "0!important" }}>
                        <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>LMP (weeks) - Optional</FormLabel>
                        <OutlinedInput
                            placeholder={itemData?.lmp === 0 ? 'N/A - Not pregnant' : 'Enter LMP in weeks'}
                            name="lmp"
                            fullWidth
                            {...register('lmp')}
                            disabled={userData.role === USER_ROLE_DEFAULT.PATIENT}
                        />
                        {errors?.lmp?.message && <FormHelperText>{errors?.lmp?.message}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid item lg={12} md={6} sm={6} xs={12} className='girdMarginBottom'>
                    <FormControl fullWidth className='inputfield' sx={{ marginTop: "0!important" }}>
                        <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>Clinical Details</FormLabel>
                        <TextField multiline rows={4} placeholder='Enter here' name="clinicalInfo" fullWidth {...register('clinicalInfo')}  disabled={userData.role === USER_ROLE_DEFAULT.PATIENT ? true :false}/>
                        {errors?.clinicalInfo?.message && <FormHelperText>{errors?.clinicalInfo?.message}</FormHelperText>}
                    </FormControl>
                </Grid>
            </Grid>
            {userData.role !== USER_ROLE_DEFAULT.PATIENT &&
            <Box sx={{ display: "flex", justifyContent: "space-between", paddingTop: "20px", marginTop: "20px" }}>
                <Button type="submit" onClick={() => { setSaveType("exit") }} className='commonBtn' variant="contained" disabled={loading}>
                    {loading ? "loading..." : 
                    itemData?.reportData?.reportStatus === "completed" ? "Update And Exit" : "Save And Exit"}
                    </Button>
                <Button type="submit" onClick={() => { setSaveType("next") }} className='commonBtn' variant="contained" disabled={loading}>
                    {loading ? "loading..." : 
                     itemData?.reportData?.reportStatus === "completed" ? "Update And Next" :"Save and Next"}
                </Button>
            </Box>
            }
        </Box>
    )
}
